import React from "react";
import { BUSINESS_TYPE_LABELS, BUSINESS_TYPE_SHORTCUTS } from "../../../core/data/businessTypes/labels";
import { BUSINESS_TYPES } from "../../../core/data/businessTypes/classifyBusiness";
import { samplingParts, snapshotTitle, STATUS_LABELS, CHANNEL_TOOLTIPS } from "../../../core/data/marketOverviewText";
import { shortJobId } from "../../../core/data/jobs";
import { REPORT, STATUS_PILL, CHANNEL_TAB } from "../../../core/theme/reportTheme";
import MultiSelect from "./MultiSelect";

// The ONE report header used by every report page (Market Overview,
// Advertiser Overview, PPC and the other channel pages, and the shared
// read-only view): job name, snapshot title (with status and #id), sampling line, the filters (device, keywords,
// postcodes, business types, advertisers - all tick boxes, nothing ticked =
// all) and the page tabs. Same labels, order and place everywhere.
// advertiserOptions: everyone seen in the job, [{ key, displayName }].
// tabs: from visiblePages() - a disabled tab shows but can't be clicked (its
// reason is the hover text). tabRight goes at the right end of the tab row
// (the pin chip).
const TYPE_OPTIONS = BUSINESS_TYPES.map(t => ({ value: t, label: BUSINESS_TYPE_LABELS[t] }));

export default function ReportHeader({ overview, job, filters, onFilters, advertiserOptions, tabs, activeId, onTab, tabRight }) {
  const keywords = job.keywords || [];
  const postcodes = job.locations || [];
  const sampling = samplingParts(overview);
  const shortId = shortJobId(overview.job.id);
  const set = (name, value) => onFilters({ ...filters, [name]: value });

  return (
    <div className={REPORT.header}>
      <div className={REPORT.bar}>
        <div className="min-w-0">
          <div className={REPORT.titleRow}>
            <h1 className={REPORT.title}>{overview.job.name || keywords.join(", ")}</h1>
          </div>
          <div className={REPORT.snapshotRow}>
            <span className={REPORT.snapshotTitle}>{snapshotTitle(overview, activeId === "advertiser" ? "advertiser" : "market")}</span>
            <span className={STATUS_PILL[overview.job.status]}>{STATUS_LABELS[overview.job.status]}</span>
            <span className={REPORT.jobId} title={`Snapshot id ${overview.job.id}`}>#{shortId}</span>
          </div>
          <p className={REPORT.subLine}>
            {sampling.plan} · <span className={REPORT.subStrong}>{sampling.searches}</span>{sampling.note && ` ${sampling.note}`}
          </p>
        </div>

        <div className={REPORT.filters}>
          {(job.devices || []).length > 0 && (
            <div className={REPORT.segmentGroup}>
              {[["all", "All"], ["mobile", "Mobile"], ["desktop", "Desktop"]].map(([id, label]) => (
                <button key={id} type="button" onClick={() => set("device", id)} className={filters.device === id ? REPORT.segmentActive : REPORT.segment}>{label}</button>
              ))}
            </div>
          )}
          <Field label="Keywords">
            <MultiSelect options={keywords} selected={filters.keywords} onChange={v => set("keywords", v)} allLabel="All keywords" noun="keyword" />
          </Field>
          <Field label="Postcodes">
            <MultiSelect options={postcodes} selected={filters.postcodes} onChange={v => set("postcodes", v)} allLabel="All postcodes" noun="postcode" />
          </Field>
          <Field label="Business type">
            <MultiSelect
              options={TYPE_OPTIONS} selected={filters.businessTypes} onChange={v => set("businessTypes", v)}
              allLabel="All types" noun="type" shortcuts={BUSINESS_TYPE_SHORTCUTS}
            />
          </Field>
          <Field label="Advertisers">
            <MultiSelect
              options={(advertiserOptions || []).map(a => ({ value: a.key, label: a.displayName }))}
              selected={filters.advertisers} onChange={v => set("advertisers", v)}
              marked={filters.highlights} onMark={v => set("highlights", v)}
              allLabel="All advertisers" noun="advertiser" searchable wide
            />
          </Field>
        </div>
      </div>

      <div className={REPORT.tabRow}>
        {tabs.map(tab => tab.disabled ? (
          <span key={tab.id} className={REPORT.tabDisabled} title={`${tab.label}: ${tab.reason}${CHANNEL_TOOLTIPS[tab.channel] ? ` · ${CHANNEL_TOOLTIPS[tab.channel]}` : ""}`} aria-disabled="true">{tab.label}</span>
        ) : (
          <button key={tab.id} type="button" onClick={() => onTab(tab.id)} className={tabClass(tab, activeId === tab.id)}>{tab.label}</button>
        ))}
        <div className={REPORT.tabRight}>{tabRight}</div>
      </div>
    </div>
  );
}

// Channel tabs are filled with their channel colour; the overview tabs are grey.
function tabClass(tab, active) {
  const colours = tab.channel && CHANNEL_TAB[tab.channel];
  if (colours) return active ? colours.active : colours.tab;
  return active ? REPORT.tabActive : REPORT.tab;
}

// A div, not a <label>: a label would pass clicks on its tick boxes to the first control.
function Field({ label, children }) {
  return (
    <div className={REPORT.field}>
      <span className={REPORT.fieldLabel}>{label}</span>
      {children}
    </div>
  );
}
